import React from "react"
import { cn } from "../lib/utils"

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "default" | "outline" | "ghost" | "destructive"
    size?: "sm" | "default" | "lg"
    asChild?: boolean
    children: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = "default", size = "default", asChild = false, children, ...props }, ref) => {
        const baseStyles =
            "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background"

        const variants = {
            default: "bg-primary text-primary-foreground hover:bg-primary/90",
            outline: "border border-input hover:bg-accent hover:text-accent-foreground",
            ghost: "hover:bg-accent hover:text-accent-foreground",
            destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        }

        const sizes = {
            sm: "h-9 px-3 text-sm",
            default: "h-10 py-2 px-4",
            lg: "h-11 px-8 text-lg",
        }

        if (asChild && React.isValidElement(children)) {
            return React.cloneElement(children as React.ReactElement, {
                className: cn(baseStyles, variants[variant], sizes[size], className),
                ref,
                ...props,
            });
        }

        return (
            <button className={cn(baseStyles, variants[variant], sizes[size], className)} ref={ref} {...props}>
                {children}
            </button>
        )
    },
)

Button.displayName = "Button"

export { Button }
